import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { LineChart, PieChart } from '@mui/x-charts';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { requestJson } from '../lib/api';
import { useMenuPage } from '../hooks/useMenuPage';

const chartColors = ['#38bdf8', '#22c55e', '#a78bfa', '#f59e0b', '#ef4444', '#14b8a6', '#60a5fa', '#f472b6'];

function defaultDateRange() {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 6);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

function csvEscape(value) {
  const text = String(value ?? '');
  return /[;"\n\r]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
}

function downloadCsv(filename, rows) {
  const content = rows.map((row) => row.map(csvEscape).join(';')).join('\n');
  const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function formatHours(value) {
  if (value === null || value === undefined || value === '') return '—';
  const number = Number(value || 0);
  if (number < 24) return `${number.toFixed(2)} h`;
  return `${(number / 24).toFixed(2)} j`;
}

function formatPeriodLabel(period) {
  if (!period) return '';
  const [year, month] = String(period).split('-');
  const date = new Date(Number(year), Number(month || 1) - 1, 1);
  if (Number.isNaN(date.getTime())) return period;
  return date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
}

function EmptyChart({ label = 'Aucune donnée.' }) {
  return (
    <Box sx={{ height: 280, display: 'grid', placeItems: 'center', color: 'text.secondary' }}>
      {label}
    </Box>
  );
}

function KpiCard({ icon, label, value, color = 'primary' }) {
  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: `${color}.light`,
              color: `${color}.contrastText`,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1 }}>
              {value}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              {label}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

const delayColorByKey = {
  supplierOrderToAnswer: '#38bdf8',
  storeAnswerToSupplierAnswer: '#f59e0b',
  requestToReturnStore: '#a78bfa',
};

function sectionTitle(icon, title, subtitle) {
  return (
    <Stack direction="row" spacing={1.2} alignItems="center">
      {icon}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 900 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography color="text.secondary" variant="body2">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

export default function LogisticsStatisticsPage() {
  const menuPage = useMenuPage('/statistiques/logistique');
  const initialRange = useMemo(defaultDateRange, []);
  const [range, setRange] = useState(initialRange);
  const [sort, setSort] = useState({ key: 'total', direction: 'desc' });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const query = new URLSearchParams(range).toString();
      setStats(await requestJson(`/statistics/logistics?${query}`));
    } catch (err) {
      setError(err.message);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const sortedStores = useMemo(() => {
    const rows = [...(stats?.byStore || [])];
    return rows.sort((a, b) => {
      const aValue = a[sort.key] ?? '';
      const bValue = b[sort.key] ?? '';
      const direction = sort.direction === 'asc' ? 1 : -1;
      if (typeof aValue === 'number' || typeof bValue === 'number') {
        return ((Number(aValue) || 0) - (Number(bValue) || 0)) * direction;
      }
      return String(aValue).localeCompare(String(bValue)) * direction;
    });
  }, [sort, stats]);

  const timelineData = useMemo(() => {
    const periods = [
      ...new Set([
        ...(stats?.timeline || []).map((item) => item.period),
        ...(stats?.delayTimeline || []).map((item) => item.period),
      ]),
    ].sort();

    return periods.map((period) => ({
      period,
      count: (stats?.timeline || []).find((item) => item.period === period)?.count || 0,
      averageHours: (stats?.delayTimeline || []).find((item) => item.period === period)?.averageHours || null,
    }));
  }, [stats]);

  const delayTimelineData = useMemo(() => {
    const timelines = stats?.delayTimelines || {};
    const periods = [
      ...new Set(
        Object.values(timelines)
          .flat()
          .map((item) => item.period)
      ),
    ].sort();

    return periods.map((period) => ({
      period,
      supplierOrderToAnswer:
        timelines.supplierOrderToAnswer?.find((item) => item.period === period)?.averageHours ?? null,
      storeAnswerToSupplierAnswer:
        timelines.storeAnswerToSupplierAnswer?.find((item) => item.period === period)?.averageHours ?? null,
      requestToReturnStore:
        timelines.requestToReturnStore?.find((item) => item.period === period)?.averageHours ?? null,
    }));
  }, [stats]);

  const activityTimelineData = useMemo(() => {
    const periods = (stats?.activityTimeline || []).map((item) => item.period);
    return periods.map((period) => {
      const item = (stats?.activityTimeline || []).find((candidate) => candidate.period === period) || {};
      return {
        period,
        storeRequests: item.storeRequests || 0,
        ordered: item.ordered || 0,
        sentToStore: item.sentToStore || 0,
      };
    });
  }, [stats]);

  const statusChartData = useMemo(
    () =>
      (stats?.byStatus || []).map((item, index) => ({
        id: item.status,
        value: item.count,
        label: item.label,
        color: chartColors[index % chartColors.length],
      })),
    [stats]
  );

  const handleSort = (key) => {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handleExportStoreCsv = () => {
    const rows = [
      ['Magasin', 'Ouverts', 'En attente réponse magasin', 'Envoyés au magasin', 'Total'],
      ...sortedStores.map((store) => [
        store.label,
        store.open || 0,
        store.waitingStore || 0,
        store.returnedStore || 0,
        store.total || 0,
      ]),
    ];
    downloadCsv(`stats-logistique-magasins-${range.from}-${range.to}.csv`, rows);
  };

  const handleExportRecordsCsv = () => {
    const rows = [
      [
        'Référence',
        'Magasin',
        'Statut',
        'Client',
        'Marque',
        'Modèle',
        'Créé le',
        'Retour final',
        'Délai commandé envoyé au magasin',
        'Délai réponse magasin réponse fournisseur',
        'Délai demande retour magasin',
      ],
      ...(stats?.records || []).map((record) => [
        record.reference,
        [record.storeCode, record.storeName].filter(Boolean).join(' - '),
        record.statusLabel,
        record.customerName,
        record.brand,
        record.model,
        record.createdAt ? new Date(record.createdAt).toLocaleString('fr-FR') : '',
        record.returnedAt ? new Date(record.returnedAt).toLocaleString('fr-FR') : '',
        record.delays?.supplierOrderToAnswer?.durationHours ?? '',
        record.delays?.storeAnswerToSupplierAnswer?.durationHours ?? '',
        record.delays?.requestToReturnStore?.durationHours ?? record.durationHours ?? '',
      ]),
    ];
    downloadCsv(`stats-logistique-dossiers-${range.from}-${range.to}.csv`, rows);
  };

  const handleExportDelayCsv = () => {
    const rows = [
      ['Indicateur', 'Dossiers mesurés', 'Délai moyen', 'Délai min', 'Délai max', 'Total heures'],
      ...(stats?.delayMetrics || []).map((metric) => [
        metric.label,
        metric.count || 0,
        metric.averageHours || 0,
        metric.minHours || 0,
        metric.maxHours || 0,
        metric.totalHours || 0,
      ]),
      [],
      ['Fournisseur', 'Dossiers mesurés', 'Délai moyen commandé envoyé au magasin', 'Délai min', 'Délai max'],
      ...(stats?.supplierDelays || []).map((supplier) => [
        supplier.supplier,
        supplier.count || 0,
        supplier.averageHours || 0,
        supplier.minHours || 0,
        supplier.maxHours || 0,
      ]),
    ];
    downloadCsv(`stats-logistique-delais-${range.from}-${range.to}.csv`, rows);
  };

  const quantitySummary = stats?.quantitySummary || {};
  const activitySummary = stats?.activitySummary || {};

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 900 }}>
            {menuPage.sectionTitle}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            {menuPage.pageTitle}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            type="date"
            label="Du"
            value={range.from}
            onChange={(event) => setRange((current) => ({ ...current, from: event.target.value }))}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            label="Au"
            value={range.to}
            onChange={(event) => setRange((current) => ({ ...current, to: event.target.value }))}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadStats}>
            Rafraîchir
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2 }}>
                <Inventory2RoundedIcon color="primary" />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    Activité mensuelle
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Demandes reçues, commandes confirmées et expéditions vers les magasins.
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
                <KpiCard icon={<StoreRoundedIcon />} label="Demandes magasin" value={activitySummary.storeRequests || 0} color="primary" />
                <KpiCard icon={<PendingActionsRoundedIcon />} label="Commandés" value={activitySummary.ordered || 0} color="secondary" />
                <KpiCard icon={<AssignmentTurnedInRoundedIcon />} label="Expédiés au magasin" value={activitySummary.sentToStore || 0} color="success" />
              </Box>

              {activityTimelineData.length ? (
                <LineChart
                  height={320}
                  xAxis={[
                    {
                      scaleType: 'point',
                      data: activityTimelineData.map((item) => item.period),
                      valueFormatter: formatPeriodLabel,
                      tickInterval: activityTimelineData.map((item) => item.period),
                      tickLabelInterval: () => true,
                      tickLabelMinGap: 0,
                      height: 58,
                      tickSize: 6,
                    },
                  ]}
                  yAxis={[{ min: 0 }]}
                  series={[
                    {
                      data: activityTimelineData.map((item) => item.storeRequests),
                      label: 'Demandes magasin',
                      color: '#38bdf8',
                      showMark: true,
                    },
                    {
                      data: activityTimelineData.map((item) => item.ordered),
                      label: 'Commandés',
                      color: '#a78bfa',
                      showMark: true,
                    },
                    {
                      data: activityTimelineData.map((item) => item.sentToStore),
                      label: 'Expédiés au magasin',
                      color: '#22c55e',
                      showMark: true,
                    },
                  ]}
                  grid={{ horizontal: true }}
                  margin={{ bottom: 28, left: 44 }}
                />
              ) : (
                <EmptyChart label="Aucune activité sur la période." />
              )}
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
                {sectionTitle(<TimerRoundedIcon color="primary" />, 'Délais Logistique', 'Mesures entre les étapes clés du workflow.')}
                <Button variant="outlined" startIcon={<FileDownloadRoundedIcon />} onClick={handleExportDelayCsv} disabled={!stats?.delayMetrics?.length}>
                  Export délais
                </Button>
              </Stack>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
                {(stats?.delayMetrics || []).map((metric) => (
                  <KpiCard
                    key={metric.key}
                    icon={<TimerRoundedIcon />}
                    label={metric.label}
                    value={formatHours(metric.averageHours)}
                    color={metric.key === 'storeAnswerToSupplierAnswer' ? 'warning' : metric.key === 'requestToReturnStore' ? 'secondary' : 'primary'}
                  />
                ))}
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.25fr 0.9fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>
                    Évolution des délais moyens
                  </Typography>
                  {delayTimelineData.length ? (
                    <LineChart
                      height={330}
                      xAxis={[
                        {
                          scaleType: 'point',
                          data: delayTimelineData.map((item) => item.period),
                          valueFormatter: formatPeriodLabel,
                          tickInterval: delayTimelineData.map((item) => item.period),
                          tickLabelInterval: () => true,
                          tickLabelMinGap: 0,
                          height: 58,
                          tickSize: 6,
                        },
                      ]}
                      yAxis={[{ min: 0, label: 'Heures' }]}
                      series={[
                        {
                          data: delayTimelineData.map((item) => item.supplierOrderToAnswer),
                          label: 'Commandé → envoyé au magasin',
                          color: delayColorByKey.supplierOrderToAnswer,
                          showMark: true,
                          connectNulls: true,
                        },
                        {
                          data: delayTimelineData.map((item) => item.storeAnswerToSupplierAnswer),
                          label: 'Réponse magasin → fournisseur',
                          color: delayColorByKey.storeAnswerToSupplierAnswer,
                          showMark: true,
                          connectNulls: true,
                        },
                        {
                          data: delayTimelineData.map((item) => item.requestToReturnStore),
                          label: 'Demande → retour magasin',
                          color: delayColorByKey.requestToReturnStore,
                          showMark: true,
                          connectNulls: true,
                        },
                      ]}
                      grid={{ horizontal: true }}
                      margin={{ bottom: 28, left: 56 }}
                    />
                  ) : (
                    <EmptyChart label="Aucun délai mesurable sur la période." />
                  )}
                </Box>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Indicateur</TableCell>
                        <TableCell align="right">Dossiers</TableCell>
                        <TableCell align="right">Moyen</TableCell>
                        <TableCell align="right">Min</TableCell>
                        <TableCell align="right">Max</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(stats?.delayMetrics || []).map((metric) => (
                        <TableRow key={metric.key}>
                          <TableCell sx={{ fontWeight: 850 }}>{metric.label}</TableCell>
                          <TableCell align="right">{metric.count || 0}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 850 }}>
                            {formatHours(metric.averageHours)}
                          </TableCell>
                          <TableCell align="right">{formatHours(metric.minHours)}</TableCell>
                          <TableCell align="right">{formatHours(metric.maxHours)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>
                  Délai Commandé → envoyé au magasin par fournisseur
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fournisseur</TableCell>
                        <TableCell align="right">Dossiers mesurés</TableCell>
                        <TableCell align="right">Délai moyen</TableCell>
                        <TableCell align="right">Min</TableCell>
                        <TableCell align="right">Max</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(stats?.supplierDelays || []).map((supplier) => (
                        <TableRow key={supplier.supplier}>
                          <TableCell sx={{ fontWeight: 850 }}>{supplier.supplier}</TableCell>
                          <TableCell align="right">{supplier.count || 0}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 850 }}>{formatHours(supplier.averageHours)}</TableCell>
                          <TableCell align="right">{formatHours(supplier.minHours)}</TableCell>
                          <TableCell align="right">{formatHours(supplier.maxHours)}</TableCell>
                        </TableRow>
                      ))}
                      {!stats?.supplierDelays?.length && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                            Aucun délai fournisseur mesurable sur la période.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </CardContent>
          </Card>

          <Divider />

          <Stack spacing={2}>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Inventory2RoundedIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Quantité de dossiers
              </Typography>
            </Stack>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              <KpiCard icon={<LocalShippingRoundedIcon />} label="Total Logistique" value={quantitySummary.total || stats?.total || 0} color="primary" />
              <KpiCard icon={<PendingActionsRoundedIcon />} label="Dossiers ouverts" value={quantitySummary.open || 0} color="warning" />
              <KpiCard icon={<MarkEmailReadRoundedIcon />} label="En attente réponse magasin" value={quantitySummary.waitingStore || 0} color="secondary" />
              <KpiCard icon={<AssignmentTurnedInRoundedIcon />} label="Envoyés au magasin" value={quantitySummary.returnedStore || 0} color="success" />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.15fr 0.85fr' }, gap: 2 }}>
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
                    Évolution du volume
                  </Typography>
                  {timelineData.length ? (
                    <LineChart
                      height={300}
                      xAxis={[
                        {
                          scaleType: 'point',
                          data: timelineData.map((item) => item.period),
                          valueFormatter: formatPeriodLabel,
                          tickInterval: timelineData.map((item) => item.period),
                          tickLabelInterval: () => true,
                          tickLabelMinGap: 0,
                          height: 58,
                          tickSize: 6,
                        },
                      ]}
                      yAxis={[{ min: 0 }]}
                      series={[
                        {
                          data: timelineData.map((item) => item.count),
                          label: 'Dossiers',
                          color: '#38bdf8',
                          showMark: true,
                        },
                      ]}
                      grid={{ horizontal: true }}
                      margin={{ bottom: 28 }}
                    />
                  ) : (
                    <EmptyChart label="Aucune évolution sur la période." />
                  )}
                </CardContent>
              </Card>

              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
                    Répartition par statut
                  </Typography>
                  {statusChartData.length ? (
                    <PieChart
                      height={300}
                      series={[
                        {
                          data: statusChartData,
                          innerRadius: 58,
                          paddingAngle: 2,
                          cornerRadius: 4,
                        },
                      ]}
                    />
                  ) : (
                    <EmptyChart />
                  )}
                </CardContent>
              </Card>
            </Box>
          </Stack>

          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <StoreRoundedIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    Répartition par magasin
                  </Typography>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    label="Trier par"
                    select
                    SelectProps={{ native: true }}
                    size="small"
                    value={sort.key}
                    onChange={(event) => setSort((current) => ({ ...current, key: event.target.value }))}
                    sx={{ minWidth: { sm: 210 } }}
                  >
                    <option value="open">Ouverts</option>
                    <option value="waitingStore">En attente réponse magasin</option>
                    <option value="returnedStore">Envoyés au magasin</option>
                    <option value="total">Total</option>
                    <option value="label">Magasin</option>
                  </TextField>
                  <TextField
                    label="Ordre"
                    select
                    SelectProps={{ native: true }}
                    size="small"
                    value={sort.direction}
                    onChange={(event) => setSort((current) => ({ ...current, direction: event.target.value }))}
                    sx={{ minWidth: { sm: 145 } }}
                  >
                    <option value="desc">Plus grand</option>
                    <option value="asc">Plus petit</option>
                  </TextField>
                  <Button variant="outlined" startIcon={<FileDownloadRoundedIcon />} onClick={handleExportStoreCsv} disabled={!sortedStores.length}>
                    Export magasins
                  </Button>
                  <Button variant="outlined" startIcon={<FileDownloadRoundedIcon />} onClick={handleExportRecordsCsv} disabled={!stats?.records?.length}>
                    Export dossiers
                  </Button>
                </Stack>
              </Stack>

              <TableContainer>
                <Table
                  size="small"
                  sx={{
                    '& .MuiTableSortLabel-root': { fontWeight: 900 },
                    '& .MuiTableSortLabel-icon': { opacity: 0.45 },
                    '& .Mui-active .MuiTableSortLabel-icon': { opacity: 1 },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {[
                        ['label', 'Magasin', 'left'],
                        ['open', 'Ouverts', 'right'],
                        ['waitingStore', 'Attente réponse magasin', 'right'],
                        ['returnedStore', 'Envoyés au magasin', 'right'],
                        ['total', 'Total', 'right'],
                      ].map(([key, label, align]) => (
                        <TableCell key={key} align={align} sortDirection={sort.key === key ? sort.direction : false}>
                          <TableSortLabel
                            active={sort.key === key}
                            direction={sort.key === key ? sort.direction : 'asc'}
                            onClick={() => handleSort(key)}
                          >
                            {label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedStores.map((store) => (
                      <TableRow key={store.storeSiteId || store.label}>
                        <TableCell sx={{ fontWeight: 850 }}>{store.label}</TableCell>
                        <TableCell align="right">{store.open || 0}</TableCell>
                        <TableCell align="right">{store.waitingStore || 0}</TableCell>
                        <TableCell align="right">{store.returnedStore || 0}</TableCell>
                        <TableCell align="right">
                          <Chip size="small" color="primary" variant="outlined" label={store.total || 0} sx={{ minWidth: 36, fontWeight: 950 }} />
                        </TableCell>
                      </TableRow>
                    ))}
                    {!sortedStores.length && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          Aucun dossier Logistique sur la période.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Stack>
  );
}
